// 帳票出力一覧
// 全 8 種類の帳票を 2 カテゴリ(発注者提出系 / 運用管理系)で提供。
// すべての帳票は ExtendedDB(IDB から組み立てた db スナップショット)ドリブン。
// 出力はオンライン限定: オフライン時はキャッシュが古い可能性があり、
// 不完全なデータを正式な明細として出力してしまう事故を防ぐ。

import { Box, Stack, Typography, Chip, Paper, Grid, Card, CardContent, Divider, Button, Alert, MenuItem, TextField } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { PageContext } from "../PageContext";
import { useFullDb } from "../../../api/dbSlice";
import { useState, useMemo } from "react";
import { reports, ReportDef, ReportCategory } from "./reports";
import { selectDefaultWorkId } from "./reports/helpers";
import { WORD } from "../../../word";
import { renderWord } from "../../../util/renderWord";
import { useIsOnline } from "../../../util/useIsOnline";
import { errorMessageOf } from "../../../util/typed";

const categoryLabels: Record<ReportCategory, string> = {
  submission: "発注者提出系",
  operation: "運用管理系",
};
const categoryDescs: Record<ReportCategory, string> = {
  submission: "発注者への提出物。",
  operation: "社内・現場での運用管理用。受払簿・使用期限・検査記録などを集約。",
};
const categoryColors: Record<ReportCategory, "primary" | "secondary"> = {
  submission: "primary",
  operation: "secondary",
};

export default function ReportListPage({ pageContext }: { pageContext: PageContext }) {
  const { db } = useFullDb(pageContext.api.indexedDB, pageContext.api.currentUser.id);
  const isOnline = useIsOnline(pageContext.api);
  const [busy, setBusy] = useState<string | null>(null);
  const [selectedWorkId, setSelectedWorkId] = useState<number | "">(
    selectDefaultWorkId(db) ?? ""
  );

  const activeWorks = useMemo(() => db.works.filter((w) => w.deleted_at == null), [db.works]);
  const grouped = useMemo(() => {
    const submission: ReportDef[] = [];
    const operation: ReportDef[] = [];
    for (const r of reports) {
      if (r.category === "submission") submission.push(r);
      else operation.push(r);
    }
    return { submission, operation };
  }, []);

  const handle = async (def: ReportDef) => {
    setBusy(def.key);
    try {
      await def.exporter(db, selectedWorkId === "" ? undefined : selectedWorkId);
    } catch (e) {
      console.error(`[Report] ${def.key} 出力エラー:`, e);
      pageContext.api.showError(`出力エラー：${errorMessageOf(e)}`);
    } finally {
      setBusy(null);
    }
  };

  const renderGroup = (cat: ReportCategory, reportList: ReportDef[]) => (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box p={3}>
        <Stack direction="row" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6" fontWeight="bold">{categoryLabels[cat]}</Typography>
          <Chip size="small" color={categoryColors[cat]} label={`${reportList.length} 件`} />
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {categoryDescs[cat]}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {reportList.map((r) => (
            <Grid size={{ xs: 12, sm: 6 }} key={r.key}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">{r.name}</Typography>
                    <Chip size="small" label="Excel" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60 }}>
                    {r.desc}
                  </Typography>
                  {r.sampleRef && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
                      参照: {r.sampleRef}
                    </Typography>
                  )}
                  <Stack direction="row" justifyContent="flex-end" mt={1}>
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={() => handle(r)} disabled={!!busy || !isOnline}>
                      {busy === r.key ? "出力中…" : "ダウンロード"}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );

  return (
    <Stack p={3} maxWidth={1200} mx="auto" gap={2}>
      <Stack direction="row" alignItems="center" gap={1}>
        <DescriptionIcon color="action" />
        <Typography variant="h4" fontWeight="bold">帳票出力</Typography>
        <Chip size="small" color="info" label="8 帳票 / Excel ダウンロード" />
      </Stack>

      <Alert severity="info">{renderWord(WORD.reportList.listInfo)}</Alert>
      {!isOnline && (
        <Alert severity="warning">オフラインのため帳票を出力できません。手元のキャッシュが最新でない可能性があり、不完全な明細の出力を防ぐためオンライン時のみ出力できます。</Alert>
      )}

      <Paper variant="outlined">
        <Box p={2}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="body2" fontWeight="bold">対象工事:</Typography>
            <TextField
              select
              size="small"
              value={selectedWorkId}
              onChange={(e) => setSelectedWorkId(e.target.value === "" ? "" : Number(e.target.value))}
              sx={{ minWidth: 400 }}
            >
              <MenuItem value="">（最初のアクティブ工事を自動選択）</MenuItem>
              {activeWorks.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.short_name || w.official_name}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              選択した工事のデータを帳票に含めます
            </Typography>
          </Stack>
        </Box>
      </Paper>

      {renderGroup("submission", grouped.submission)}
      {renderGroup("operation", grouped.operation)}
    </Stack>
  );
}
